import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CreditCard, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { errorDetail } from './mcpServerRegistry';

/** Mirrors StripeConnectService.serialize (backend). */
export interface ConnectedAccount {
  id: string;
  stripe_account_id: string;
  livemode: boolean;
  status: 'pending' | 'active' | 'restricted' | 'disconnected';
  details_submitted: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  country: string | null;
  default_currency: string | null;
  requirements_due_codes: string[];
  disabled_reason: string | null;
  verification_pending: boolean;
  dashboard_url: string | null;
  last_synced_at: string | null;
  disconnected_at: string | null;
}

export interface CommerceAccountResponse {
  enabled: boolean;
  livemode: boolean;
  supported_countries: string[];
  supported_currencies: string[];
  account: ConnectedAccount | null;
  charge_capable: boolean;
}

const STATUS_LABEL: Record<ConnectedAccount['status'], { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending: { label: 'Onboarding incomplete', variant: 'secondary' },
  active: { label: 'Ready to accept payments', variant: 'default' },
  restricted: { label: 'Action required', variant: 'destructive' },
  disconnected: { label: 'Disconnected', variant: 'outline' },
};

/** Friendly copy for Stripe's `requirements.disabled_reason` codes. */
const DISABLED_REASON_COPY: Record<string, string> = {
  'requirements.past_due': 'Stripe needs additional information that is now overdue. Finish it in your Stripe Dashboard to keep accepting payments.',
  'requirements.pending_verification': 'Stripe is verifying your details.',
  'rejected.fraud': 'Stripe has rejected this account. Contact Stripe support from your Dashboard.',
  'rejected.terms_of_service': 'Stripe has rejected this account for a terms-of-service issue. Contact Stripe support from your Dashboard.',
  'rejected.listed': 'Stripe has rejected this account. Contact Stripe support from your Dashboard.',
  'rejected.other': 'Stripe has rejected this account. Contact Stripe support from your Dashboard.',
  listed: 'Stripe has paused this account pending review. Contact Stripe support from your Dashboard.',
  under_review: 'Stripe is reviewing this account. Payments resume when the review completes.',
  other: 'Stripe has paused this account. Check your Stripe Dashboard for details.',
};

const disabledReasonCopy = (code: string) =>
  DISABLED_REASON_COPY[code] ?? 'Stripe has paused this account. Check your Stripe Dashboard for details.';

/**
 * Integrations → Stripe payments: publisher Stripe Connect onboarding
 * (SERVICES_COMMERCE_ADR.md A3). The return redirect proves nothing — we
 * always re-sync from Stripe on return, and the backend only lets prices be
 * activated once the account is charge-capable.
 */
const StripePayments: React.FC = () => {
  const { get, post } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<CommerceAccountResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await get('/services/commerce/stripe/account');
      setData(response.data as CommerceAccountResponse);
    } catch (err: any) {
      toast.error(errorDetail(err, 'Could not load Stripe status.'));
    } finally {
      setLoading(false);
    }
  }, [get]);

  const sync = useCallback(async (quiet = false) => {
    setSyncing(true);
    try {
      const response = await post('/services/commerce/stripe/sync', {});
      setData((current) => (current ? { ...current, ...response.data } : current));
      if (!quiet) toast.success('Stripe status refreshed');
    } catch (err: any) {
      if (!quiet) toast.error(errorDetail(err, 'Could not refresh Stripe status.'));
    } finally {
      setSyncing(false);
    }
  }, [post]);

  useEffect(() => {
    load();
  }, [load]);

  // Returned from Stripe-hosted onboarding: resync, then drop the marker.
  const stripeReturn = searchParams.get('stripe');
  useEffect(() => {
    if (!stripeReturn || loading) return;
    sync(true).finally(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('stripe');
      setSearchParams(next, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeReturn, loading]);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const response = await post('/services/commerce/stripe/onboarding', { country: 'US' });
      window.location.assign(response.data.url as string);
    } catch (err: any) {
      toast.error(errorDetail(err, 'Could not start Stripe onboarding.'));
      setStarting(false);
    }
  };

  const disconnect = async () => {
    setConfirmDisconnect(false);
    try {
      const response = await post('/services/commerce/stripe/disconnect', {});
      setData((current) => (current ? { ...current, ...response.data } : current));
      toast.success('Stripe account disconnected');
    } catch (err: any) {
      toast.error(errorDetail(err, 'Could not disconnect.'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Stripe status…
      </div>
    );
  }

  if (!data?.enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" /> Stripe payments
          </CardTitle>
          <CardDescription>
            Sell services through your own Stripe account. Services commerce is not enabled for this
            organization yet — contact Aimdoc to turn it on.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const account = data.account;
  const status = account
    ? account.verification_pending
      ? { label: 'Pending verification', variant: 'secondary' as const }
      : STATUS_LABEL[account.status]
    : null;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" /> Stripe payments
                {!data.livemode && <Badge variant="outline">Test mode</Badge>}
              </CardTitle>
              <CardDescription className="mt-1">
                Buyers pay you directly through your Stripe account. You stay the merchant of record and
                handle receipts, refunds, and disputes in your Stripe Dashboard; Aimdoc keeps service
                access in sync and collects a 5% platform fee on each successful payment.
              </CardDescription>
            </div>
            {status && <Badge variant={status.variant}>{status.label}</Badge>}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!account && (
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={startOnboarding} disabled={starting}>
                {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Set up Stripe payments
              </Button>
              <p className="text-sm text-muted-foreground">
                Stripe collects your business, identity, and bank details on its own hosted pages. Aimdoc
                never sees them. Available for {data.supported_countries.join(', ')} businesses.
              </p>
            </div>
          )}

          {account && (
            <>
              <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground">Account</dt>
                  <dd className="font-mono">{account.stripe_account_id}</dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground">Country / currency</dt>
                  <dd>
                    {account.country ?? '—'} / {(account.default_currency ?? '—').toUpperCase()}
                  </dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground">Charges</dt>
                  <dd>{account.charges_enabled ? 'Enabled' : 'Not yet enabled'}</dd>
                </div>
                <div className="flex justify-between sm:block">
                  <dt className="text-muted-foreground">Payouts</dt>
                  <dd>{account.payouts_enabled ? 'Enabled' : 'Not yet enabled'}</dd>
                </div>
              </dl>

              {account.verification_pending && (
                <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                  Stripe is verifying the details you submitted. This usually takes minutes, occasionally a
                  day or two; nothing to do on your side. We&apos;ll switch this on automatically once Stripe
                  enables charges.
                </div>
              )}
              {account.requirements_due_codes.length > 0 && account.status !== 'disconnected' && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Stripe still needs: {account.requirements_due_codes.join(', ')}. Continue onboarding to
                  finish.
                </div>
              )}
              {account.disabled_reason && account.status === 'restricted' && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                  {disabledReasonCopy(account.disabled_reason)}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {account.status !== 'disconnected' && account.status !== 'active' && !account.verification_pending && (
                  <Button onClick={startOnboarding} disabled={starting}>
                    {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Continue onboarding
                  </Button>
                )}
                {account.dashboard_url && account.status !== 'disconnected' && (
                  <Button variant="outline" asChild>
                    <a href={account.dashboard_url} target="_blank" rel="noreferrer">
                      Open Stripe Dashboard <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                )}
                <Button variant="ghost" onClick={() => sync()} disabled={syncing}>
                  <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> Refresh status
                </Button>
                {account.status !== 'disconnected' && (
                  <Button
                    variant="ghost"
                    className="text-destructive"
                    onClick={() => setConfirmDisconnect(true)}
                  >
                    Disconnect
                  </Button>
                )}
              </div>
              {account.last_synced_at && (
                <p className="text-xs text-muted-foreground">
                  Last checked {new Date(account.last_synced_at).toLocaleString()}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Stripe?</AlertDialogTitle>
            <AlertDialogDescription>
              New checkouts and price activation stop immediately. Existing subscriptions and orders keep
              their own lifecycle, and your Stripe account itself is not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={disconnect}>Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StripePayments;
